// Copyright 2019 Drivetrain Hub LLC

import Vue from 'vue'
import Router from 'vue-router'
import urls from "./urls";

// lazy-loading views (async components)
const GearsApp = () => import('../views/GearsApp');
const FileExplorer = () => import ('../components/FileExplorer');
const Modeling = () => import ('../components/Modeling');
const Results = () => import ('../components/viewer/Results');
const ErrorNotFound = () => import('../views/ErrorNotFound');
const ErrorForbidden = () => import('../views/ErrorForbidden');
const ErrorBadRequest = () => import('../views/ErrorBadRequest');
const ErrorMobile = () => import('../views/ErrorMobile');
const ApolloPlayground = () => import('../views/ApolloPlayground');

// region HELPERS

/**
 * Create a URL string with a next parameter for redirects.
 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 * @param toUrl {string} Base URL string.
 * @param nextUrl {string} URL string to specify in next parameter.
 * @return {string} URL string with base URL and next param.
 */
function urlNext (toUrl, nextUrl) {
  let url = new URL(toUrl);
  url.searchParams.set('next', nextUrl);
  // console.log(url);  // DEV ONLY
  return url.href
}

// endregion

// region ROUTER

Vue.use(Router);

export const router = new Router({
  // PLACEHOLDER (routing) verify server-side implementation of 'history' mode
  // https://router.vuejs.org/guide/essentials/history-mode.html
  mode: 'history',
  routes: [
    {
      path: '/',
      redirect: { name: 'fileExplorer' },
      component: GearsApp,
      children: [
        {
          path: '/models',
          name: 'fileExplorer',
          component: FileExplorer,
          meta: {
            title: 'File Explorer'
          }
        },
        {
          path: '/model/:modelId',
          name: 'openModel',
          component: Modeling,
          meta: {
            title: 'Model'
          },
          // PLACEHOLDER {results} implement URL routing to load model results (closable tab or new view)
          children: [
            {
              path: 'r/:resultsId',
              name: 'openModelResults',
              component: Results,
            }
          ],
        },
        {
          path: '/model/:modelId/v/:versionId',
          name: 'openRelease',
          component: Modeling,
          meta: {
            title: 'Model Release'
          },
          // PLACEHOLDER {results} implement URL routing to load model release results (closable tab or new view)
          children: [
            {
              path: 'r/:resultsId',
              name: 'openReleaseResults',
              component: Results,
            }
          ],
        },
      ]
    },
    {
      path: '/signin',
      name: 'signin',
      beforeEnter: () => {
        // redirect to external SSO URL with next param pointing to current URL or home route
        const currentUrl = window.location.href;
        const nextUrl = currentUrl.includes('signin') ? process.env.VUE_APP_HOST : currentUrl;
        const signinUrl = urlNext(urls.USER_SIGNIN, nextUrl);
        location.replace(signinUrl);
      }
    },
    {
      path: '/signout',
      name: 'signout',
      beforeEnter() {
        // redirect to SSO external URL
        location.replace(urls.USER_SIGNOUT)
      },
    },
    {
      path: '/apollo',
      name: 'apollo',
      component: ApolloPlayground,
      beforeEnter(to, from, next) {
        if (process.env.NODE_ENV === "production") {
          router.push({name: 'notFound'});
        } else {
          next()
        }
      }
    },
    {
      path: '/403',
      name: 'forbidden',
      component: ErrorForbidden,
      meta: {
        title: 'Forbidden'
      },
    },
    {
      path: '/400',
      name: 'badRequest',
      component: ErrorBadRequest,
      meta: {
        title: 'Bad Request'
      },
    },
    {
      // catch-all for non-existent routes
      // https://router.vuejs.org/guide/essentials/history-mode.html#caveat
      path: '/404',
      alias: '*',
      name: 'notFound',
      component: ErrorNotFound,
      meta: {
        title: 'Not Found'
      },
    },
    {
      path: '/mobile',
      name: 'mobile',
      component: ErrorMobile,
      meta: {
        title: 'Mobile'
      },
    },
  ],
});

router.beforeEach((to, from, next) => {
  if (to.meta.title) {
    document.title = 'Gears App - ' + to.meta.title;
  } else {
    document.title = 'Gears App'
  }
  next()
});

// endregion

// region APP FUNCTIONS

/**
 * Display a route component without changing the URL.  Useful for displaying errors.
 * Be careful using this, since it does not push a new route, thus browser back button behavior may be unexpected.
 * @param route - Route path, e.g. '/403', or object, e.g. {name: 'forbidden'} to display.
 */
export function displayRoute(route) {
  router.history.updateRoute(router.match(route));
}

// endregion

// PLACEHOLDER {improve} consider for future implementation; docs do not explain usage
// https://router.vuejs.org/api/#router-onerror
// router.onError(callback);
